const seatsDictionary = require('./dictionaries/seats');
const commonDictionary = require('./dictionaries/common');
const seatMapSelectionCollection = require('../../modules/collections/seatmap_preferences_selections.collection').getInstance();
const trackingUtils = require('od-modules/utils/tracking.utils');

const Tracking = require('./tracking_base');

const SEARCH_REQUEST_TYPE = {
    ONE_WAY: 'OW',
    ROUND_TRIP: 'RT',
    MULTIPLE_DESTINATIONS: 'MD',
};

const FLOW_TYPE_CATEGORY = {
    ONLINE: null,
    POST_BOOKING: 'pb_seatmap',
    CHECKIN: 'pb_checkin_seatadd_page',
};

const PROVIDER_TYPE = {
    'GDS': 'gds',
    'TF': 'tf',
    'VLG': 'vlg',
    'XFARE': 'xfare',
    'NA': 'noavail',
};

let instance;
const ACTION_KEY = 'seats';

class SeatsTracking extends Tracking {
    constructor() {
        const dictionary = Odigeo._.merge({}, commonDictionary, seatsDictionary);
        super(dictionary, ACTION_KEY);
    }

    setInitialTrackingData(flowType, trackingData) {
        this.flowType = flowType;
        this.eventCategory = FLOW_TYPE_CATEGORY[flowType];
        this.flightSections = trackingData.numSections;
        this.availableSections = trackingData.availableSections;
        this.searchType = trackingData.searchType;
        this.provider = PROVIDER_TYPE[
            this.flowType === 'ONLINE'
                ? Tracking.getSelectedItineraryProvider()
                : Tracking.getProviderFromBookingInfo(trackingData.bookingInfo)
        ];
        if (flowType === 'ONLINE') {
            const searchParameters = Odigeo.Utils.parseJson(Odigeo.Session.get('searchParameters')) || {};
            this.numPassengers = searchParameters.numAdults + searchParameters.numChildren + searchParameters.numInfants;
            this.numAllowedPassengers = searchParameters.numAdults + searchParameters.numChildren;
        } else {
            this.numPassengers = trackingData.numTravellers;
            this.numAllowedPassengers = trackingData.numAllowedTravellers;
        }
    }

    setIsSeatmapOnContinuance(isSeatmapOnContinuance) {
        this.isSeatmapOnContinuance = isSeatmapOnContinuance;
    }

    getIsSeatmapOnContinuance() {
        return this.isSeatmapOnContinuance;
    }

    setAvailableSections(availableSections) {
        this.availableSections = availableSections;
    }

    setSeatMapMessage(seatMapMessage) {
        this.seatMapMessage = seatMapMessage;
    }

    getPrimeInfo() {
        return this.primeInfo || {};
    }

    setPrimeInfo(primeInfo) {
        this.primeInfo = primeInfo;
    }

    areAllSeatsSelected() {
        const selectedSeats = seatMapSelectionCollection.getSelectedSeatsLength();
        return (this.numAllowedPassengers * this.availableSections) === selectedSeats;
    }

    getPrime() {
        const primeInfo = this.getPrimeInfo();

        if (primeInfo.isPrimeView) {
            return primeInfo.isPrimeUser ? 'yes' : 'no';
        }

        return 'na';
    }

    getMaxdsc() {
        const primeInfo = this.getPrimeInfo();
        const valueKey = primeInfo.discountDisplayType === 'per' ? 'maxDiscountPercentage' : 'maxDiscountAmount';

        return `${primeInfo[valueKey] || 0}-${primeInfo.discountDisplayType || 'per'}`;
    }

    getLoadTrackingParams(withPrimeInfo = false) {
        return {
            name: 'seat-widget',
            loaded: {
                flightSections: this.flightSections,
                availableSections: this.availableSections,
            },
            meta: Odigeo.SessionScope.isMeta ? 1 : 0,
            type: SEARCH_REQUEST_TYPE[this.searchType],
            prov: this.provider,
            pax: this.numPassengers,
            mes: this.seatMapMessage,
            ...(withPrimeInfo ? {
                prime: this.getPrime(),
                maxdsc: this.getMaxdsc(),
            } : {}),
        };
    }
    getSeatsWidgetClickTrackingParams() {
        const scen = this.areAllSeatsSelected() ? 'all' : seatMapSelectionCollection.hasSelectedAnySeat() ? 'some' : '0';

        return {
            name: 'seat-widget',
            meta: Odigeo.SessionScope.isMeta ? 1 : 0,
            type: SEARCH_REQUEST_TYPE[this.searchType],
            prov: this.provider,
            pax: this.numPassengers,
            scen,
            totleg: this.flightSections,
            leg: {
                availableSections: this.availableSections,
                nSectionsWithSeatsAdded: seatMapSelectionCollection.getNumberOfSectionsWithSeatsSelected(),
            },
            mes: this.seatMapMessage,
            prime: this.getPrime(),
            maxdsc: this.getMaxdsc(),
        };
    }
    getSelectSeatTrackingParams() {
        return {
            meta: Odigeo.SessionScope.isMeta ? 1 : 0,
            type: SEARCH_REQUEST_TYPE[this.searchType],
            prov: this.provider,
            pax: this.numPassengers,
            totleg: this.flightSections,
            leg: {
                availableSections: this.availableSections,
            },
            mes: this.seatMapMessage,
            prime: this.getPrime(),
            maxdsc: this.getMaxdsc(),
        };
    }
    getNavigateSectionTrackingParams() {
        const scen = this.areAllSeatsSelected() ? 'NA' : '1';

        return {
            meta: Odigeo.SessionScope.isMeta ? 1 : 0,
            type: SEARCH_REQUEST_TYPE[this.searchType],
            prov: this.provider,
            pax: this.numPassengers,
            scen,
            totleg: this.flightSections,
            leg: {
                availableSections: this.availableSections,
            },
            mes: this.seatMapMessage,
        };
    }
    getLegendTrackingParams() {
        const scen = this.areAllSeatsSelected() ? 'NA' : '1';

        return {
            meta: Odigeo.SessionScope.isMeta ? 1 : 0,
            type: SEARCH_REQUEST_TYPE[this.searchType],
            prov: this.provider,
            pax: this.numPassengers,
            scen,
            totleg: this.flightSections,
            leg: this.availableSections,
            mes: this.seatMapMessage,
        };
    }
    getSeatsContinuanceTrackingParams() {
        const pag = Odigeo.SessionScope.flowProduct === 'dp' ? 'dp' : 'fl';

        return {
            name: `num_seats:${seatMapSelectionCollection.getNumberOfSectionsWithSeatsSelected()}`,
            meta: Odigeo.SessionScope.isMeta ? 1 : 0,
            type: SEARCH_REQUEST_TYPE[this.searchType],
            prov: this.provider,
            pax: this.numPassengers,
            paxs: seatMapSelectionCollection.getNumberOfPassengersWithSeatsSelected(),
            totleg: this.flightSections,
            leg: this.availableSections,
            mes: this.seatMapMessage,
            prime: this.getPrime(),
            maxdsc: this.getMaxdsc(),
            pag,
        };
    }

    trackSeatsOnLoad() {
        this.trackSeatsEvent({
            action: {
                name: 'on_load_widgets',
            },
            label: {
                name: 'loaded',
                params: {
                    name: 'seat-widget_loaded',
                },
            },
        });
        this.trackSeatsEvent({
            action: {
                name: 'on_load_widgets',
            },
            label: {
                name: 'loadedAllInfo',
                params: this.getLoadTrackingParams(true),
            },
        });
    }

    trackSeatsOnLoadError(errorReason) {
        const loadedParams = this.getLoadTrackingParams();
        loadedParams.name = `seat-widget_NOTloaded:${errorReason}`;
        this.trackSeatsEvent({
            action: {
                name: 'on_load_widgets',
            },
            label: {
                name: 'loadedError',
                params: {
                    name: 'seat-widget_NOTloaded',
                },
            },
        });
        this.trackSeatsEvent({
            action: {
                name: 'on_load_widgets',
            },
            label: {
                name: 'loadedErrorAllInfo',
                params: loadedParams,
            },
        });
    }
    trackSeatsOnLoadLast(currentSection) {
        const params = this.getLoadTrackingParams();
        params.loaded.currentSection = `${currentSection}`;
        params.name = 'seat-widget_last';

        this.trackSeatsEvent({
            action: {
                name: 'on_load_widgets',
            },
            label: {
                name: 'loadedLast',
                params,
            },
        });
    }

    trackSeatsOnLoadNoSelection() {
        const params = this.getLoadTrackingParams();
        params.name = 'seat-widget_noselection';
        this.trackSeatsEvent({
            action: {
                name: 'on_load_widgets',
            },
            label: {
                name: 'loadedNoSelection',
                params,
            },
        });
    }

    trackSeatsWidgetClick(click) {
        const params = this.getSeatsWidgetClickTrackingParams();
        params.click = click;

        if (click !== 'confirm' || params.leg?.nSectionsWithSeatsAdded) {
            this.trackSeatsEvent({
                action: {
                    name: 'selection',
                    params: {
                        name: 'seat',
                    },
                },
                label: {
                    name: 'openWidget',
                    params: {
                        name: params.name,
                        click: params.click,
                    },
                },
            });
        }

        this.trackSeatsEvent({
            action: {
                name: 'selection',
                params: {
                    name: 'seat',
                },
            },
            label: {
                name: 'openWidgetAllInfo',
                params,
            },
        });
    }

    trackSelectSeat(seatSelection, click) {
        const params = this.getSelectSeatTrackingParams();
        params.paxs = seatSelection.passenger;
        params.leg.selectedSection = seatSelection.section;
        params.name = `seat_${click}`;

        this.trackSeatsEvent({
            action: {
                name: 'selection',
                params: {
                    name: 'seat',
                },
            },
            label: {
                name: 'selectSeat',
                params,
            },
        });
    }

    trackClearSelection() {
        const params = this.getSelectSeatTrackingParams();
        params.paxs = seatMapSelectionCollection.getNumberOfPassengersWithSeatsSelected();
        params.leg.selectedSections = seatMapSelectionCollection.getNumberOfSectionsWithSeatsSelected();
        params.name = 'seat_clear';

        this.trackSeatsEvent({
            action: {
                name: 'selection',
                params: {
                    name: 'seat',
                },
            },
            label: {
                name: 'clearSelection',
                params,
            },
        });
    }
    trackNavigateSection(direction, nextSection) {
        const params = this.getNavigateSectionTrackingParams();
        params.paxs = seatMapSelectionCollection.getNumberOfPassengersWithSeatsSelected();
        params.leg.nextSection = nextSection + 1;
        params.name = `seat_${direction}`;

        this.trackSeatsEvent({
            action: {
                name: 'selection',
                params: {
                    name: 'seat',
                },
            },
            label: {
                name: 'navigateSection',
                params,
            },
        });
    }
    trackTapLegend(seatType) {
        const params = this.getLegendTrackingParams();
        params.name = `seat_legend-${seatType}`;

        this.trackSeatsEvent({
            action: {
                name: 'selection',
                params: {
                    name: 'seat',
                },
            },
            label: {
                name: 'tapLegend',
                params,
            },
        });
    }
    trackTapLegendMobile(action) {
        const params = this.getLegendTrackingParams();
        params.name = `seat_${action}:legend`;

        this.trackSeatsEvent({
            action: {
                name: 'selection',
                params: {
                    name: 'seat',
                },
            },
            label: {
                name: 'tapLegend',
                params,
            },
        });
    }
    trackShowDetails(action) {
        const params = {
            name: `seat_moreinfo:${action}`,
            pag: 'pbseatmap',
            leg: {
                flightSections: this.flightSections,
                selectedSections: seatMapSelectionCollection.getNumberOfSectionsWithSeatsSelected(),
            },
        };
        this.trackSeatsEvent({
            action: {
                name: 'pb_widget_seats',
            },
            label: {
                name: 'showDetails',
                params,
            },
        });
    }
    trackSeatsContinuance(page) {
        const params = this.getSeatsContinuanceTrackingParams();
        params.pag = `${page}${params.pag}`;

        this.trackSeatsEvent({
            action: {
                name: 'continuance',
                params: {
                    name: 'seats',
                },
            },
            label: {
                name: 'continuance',
                params: {
                    name: params.name,
                    pag: params.pag,
                },
            },
        });
        this.trackSeatsEvent({
            action: {
                name: 'continuance',
                params: {
                    name: 'seats',
                },
            },
            label: {
                name: 'continuanceAllInfo',
                params,
            },
        });
    }
    trackSeatsEvent(eventData) {
        const eventCategory = this.getIsSeatmapOnContinuance() ? 'flights_seatmap' : this.eventCategory;
        if (eventCategory) {
            this.trackEventWithCategory(eventData, eventCategory);
        } else {
            this.trackEvent(eventData);
        }
    }
    trackSeatmapPage() {
        trackingUtils.trackPage('seatmap');
    }
    trackContinueWithRandomSeats() {
        this.trackSeatsWidgetClick('continue-random');
    }
    trackSummaryPopupOnload() {
        trackingUtils.trackEventWithCategory('flights_seatmap', 'nags', 'nag_seats_confirm_seatmap_appearances');
    }
    trackSummaryPopupUnderstood() {
        trackingUtils.trackEventWithCategory('flights_seatmap', 'nags', 'nag_seats_confirm_seatmap_ok');
    }
}

module.exports = SeatsTracking;
module.exports.getInstance = () => {
    if (Odigeo._.isUndefined(instance)) {
        instance = new SeatsTracking();
    }

    return instance;
};
